import { type ComponentPropsWithoutRef, type ElementRef, forwardRef, type ReactNode } from "react";
import { cn } from "@/core/shared/v2/lib/utils";
import { Checkbox } from "@/core/shared/v2/ui/checkbox";
import { Label } from "@/core/shared/v2/ui/label";

export interface CheckboxFieldProps extends ComponentPropsWithoutRef<typeof Checkbox> {
  label: ReactNode;
  error?: string;
  /** className for the outer wrapper (the `className` prop goes to the Checkbox itself). */
  wrapperClassName?: string;
  /** className for the Label (text size/color). */
  labelClassName?: string;
}

/**
 * Presentational: v2 Checkbox + Label + error text. Intentionally has NO built-in
 * Controller — the caller wires `checked`/`onCheckedChange` (often with setValue
 * side-effects), keeping the consent-sync logic explicit at the call site.
 */
export const CheckboxField = forwardRef<ElementRef<typeof Checkbox>, CheckboxFieldProps>(
  ({ label, error, wrapperClassName, labelClassName, id, name, ...checkboxProps }, ref) => {
    const fieldId = id ?? name;

    return (
      <div className={cn("flex flex-col", wrapperClassName)}>
        <div className="flex items-start gap-2">
          <Checkbox ref={ref} id={fieldId} name={name} {...checkboxProps} />
          <Label htmlFor={fieldId} className={cn("font-normal leading-snug", labelClassName)}>
            {label}
          </Label>
        </div>
        {error && (
          <p role="alert" className="mt-1 font-medium text-destructive text-sm">
            {error}
          </p>
        )}
      </div>
    );
  },
);

CheckboxField.displayName = "CheckboxField";
